import { useEffect, useState } from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge, money } from "../../components/dashboard/Shared";

const REPORTS = [
  { type: "daily", title: "Daily Report", desc: "Today's trades and P&L snapshot." },
  { type: "monthly", title: "Monthly Report", desc: "Month-to-date performance and full trade list." },
  { type: "statement", title: "Account Statement", desc: "Complete account statement with all closed trades." },
  { type: "performance", title: "Performance Summary", desc: "Key metrics: returns, drawdown, win rate, fees." },
];

export default function Reports() {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/investor/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
  }, []);

  const download = async (type) => {
    setBusy(type);
    try {
      const res = await api.get(`/investor/reports/${type}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `chartzfx_${type}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Connect an MT5 account first to generate reports");
    } finally { setBusy(""); }
  };

  return (
    <div>
      <PageHeader title="Reports" sub="Institutional-grade PDF reporting, generated on demand" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {REPORTS.map((r) => (
          <div key={r.type} className="card-dark card-dark-hover p-5 flex flex-col">
            <p className="text-white text-sm font-medium">{r.title}</p>
            <p className="text-xs text-gray-500 mt-1 flex-1">{r.desc}</p>
            <button data-testid={`download-${r.type}-report`} onClick={() => download(r.type)} disabled={busy === r.type}
              className="btn-outline mt-4 py-2 text-xs flex items-center justify-center gap-2 disabled:opacity-50">
              <FileDown size={14} /> {busy === r.type ? "Generating…" : "Download PDF"}
            </button>
          </div>
        ))}
      </div>
      <p className="label-xs mb-4">Profit Sharing Invoices</p>
      {invoices.length === 0 ? (
        <p className="text-sm text-gray-500">No invoices yet.</p>
      ) : (
        <Table headers={["Invoice", "Period", "Net Profit", "Fee Rate", "Fee Amount", "Due Date", "Status"]} testId="investor-invoices-table">
          {invoices.map((i) => (
            <tr key={i.invoice_id} className="border-b border-white/5">
              <Td className="tabular text-white">{i.invoice_id}</Td>
              <Td className="tabular">{i.period}</Td>
              <Td className="tabular text-[#00E676]">{money(i.profit)}</Td>
              <Td className="tabular">{i.fee_rate}%</Td>
              <Td className="tabular text-white">{money(i.fee_amount)}</Td>
              <Td className="tabular text-gray-500">{i.due_date}</Td>
              <Td><StatusBadge status={i.status} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}
